import { useInfiniteQuery } from "@tanstack/react-query";
import { getMyNotifications } from "@/modules/notification-center/api";
import { NotificationQueryParams } from "@/modules/notification-center/dtos";
import useAuth from "@/modules/common/hooks/auth.hook";

export const useNotificationsList = ({
  params,
}: {
  params: NotificationQueryParams;
}) => {
  const { user } = useAuth();
  const {
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useInfiniteQuery({
    queryKey: [
      "my-notifications",
      {
        pageSize: params.pageSize,
        read: params.read,
        systemTemplateId: params.systemTemplateId,
        createdAtMin: params.createdAtMin,
        createdAtMax: params.createdAtMax,
      },
    ],
    queryFn: async ({ pageParam = 1 }) => {
      return await getMyNotifications({
        page: pageParam,
        pageSize: params.pageSize ?? 10,
        read: params.read,
        systemTemplateId: params.systemTemplateId,
        createdAtMin: params.createdAtMin,
        createdAtMax: params.createdAtMax,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalPages = lastPage.pages;
      const currentPage = allPages.length;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    enabled: !!user,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const count = data?.pages[0]?.count ?? 0;
  const notifications = data?.pages.flatMap((page) => page.data) ?? [];
  const isNotificationsLoading = isLoading || isFetching || isFetchingNextPage;

  return {
    notifications,
    isNotificationsLoading,
    hasNextPage: !!hasNextPage,
    fetchNextPage,
    refetch,
    count,
  };
};
